import {yupResolver} from '@hookform/resolvers/yup';
import {isEmpty} from 'lodash';
import {useEffect} from 'react';
import {useForm, Controller} from 'react-hook-form';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import * as yup from 'yup';
import {
  useAddCodingProblem,
  useUpdateProblemMutation,
} from '../../api/codingChallenge';
import {invalidateQueryV2} from '../../services/reactQuery';
import {difficultyLevelOptions} from '../../utils/constants';
import SubjectDropdown from '../common/SubjectDropdown';
import FormItem from '../jobForm/FormItem';
import Button from '../ui/Button';
import InputField from '../ui/InputField';
import RichTextEditor from '../ui/RichTextEditor';
import {SelectFieldHookForm} from '../ui/SelectField';
import {Switch} from '@headlessui/react';

const codingQuestionSchema = yup.object().shape({
  Name: yup.string().required('Question Name is required'),
  Difficulty: yup.object().nullable(),
  Score: yup.string().required('Score is required'),
  Duration: yup
    .number()
    .required('Duration is required')
    .min(1, 'Duration must be at least 1 minute')
    .max(180, 'Duration cannot exceed 180 minutes')
    .typeError('Duration is required'),
  Subject: yup.array(),
  Description: yup.string(),
  Constrain: yup.string(),
  Example: yup.string(),
  is_this_query: yup.boolean(),
});

const CodingQuestion = ({problemData, editMode = false, problemId}) => {
  const navigate = useNavigate();
  const {mutate: addQuestionMutation, isLoading: isAddQuestionLoading} =
    useAddCodingProblem();
  const {mutate: updateQuestionMutation, isLoading: isUpdateQuestionLoading} =
    useUpdateProblemMutation();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(codingQuestionSchema),
    defaultValues: {
      Name: '',
      Difficulty: null,
      Score: '',
      Duration: 15, // Default duration in minutes
      Subject: [],
      Constrain: '',
      Description: '',
      Example: '',
      is_this_query: false,
    },
  });

  useEffect(() => {
    if (!isEmpty(problemData)) {
      const {
        name,
        score,
        difficulty,
        testSubjectId,
        constraints,
        example,
        description,
        duration,
        is_this_query,
      } = problemData;
      const subjects = testSubjectId.map((sub) => {
        return {
          label: sub.subject,
          value: sub.subject,
          id: sub._id,
        };
      });
      setValue('Name', name || '');
      setValue('Score', score || '');
      setValue('Duration', duration || 60);
      setValue(
        'Difficulty',
        difficultyLevelOptions.find((x) => x.value == difficulty) || null,
      );
      setValue('Subject', subjects || []);
      setValue('Constrain', constraints || '');
      setValue('Description', description || '');
      setValue('Example', example || '');
      setValue('is_this_query', is_this_query || false);
    }
  }, [problemData]);

  const handleAddQuestion = (values) => {
    const subjectIds = values.Subject.map((sub) => sub.id);
    const payload = {
      name: values.Name,
      testSubjectId: subjectIds,
      description: values.Description,
      example: values.Example,
      constraints: values.Constrain,
      difficulty: values.Difficulty?.value || null,
      score: values.Score,
      duration: values.Duration,
      is_this_query: values.is_this_query,
    };

    const mutationOptions = {
      onSuccess: () => {
        toast.success(
          `Question ${editMode ? 'updated' : 'added'} Successfully`,
        );
        reset();
        invalidateQueryV2(['problem']);
        navigate('/coding-challenge');
      },
      onError: (data) => toast.error(data.response.data.message),
    };

    if (editMode) {
      const updatedPayload = {
        ...payload,
        problemId,
      };
      updateQuestionMutation(updatedPayload, mutationOptions);
    } else {
      addQuestionMutation(payload, mutationOptions);
    }
  };
  return (
    <div className="px-8 py-6">
      <button
        onClick={() => navigate('/coding-challenge')}
        className="bg-[#0284c7] text-white px-4 py-2 ml-2 flex"
      >
        Go Back
      </button>
      <form className="mt-3 px-2" onSubmit={handleSubmit(handleAddQuestion)}>
        <div className="flex bg-white flex-col gap-6 p-5">
          <div className="flex border-b-2 border-gray-200 py-2 ">
            <div className="text-[24px] font-bold">
              {editMode ? 'Edit Coding question' : 'Add Coding Question'}{' '}
            </div>
          </div>
          <div className="flex gap-4">
            <div className="basis-1/2">
              <FormItem label="Question Name" htmlFor="questionNamey">
                <InputField
                  {...register('Name')}
                  name="Name"
                  type="text"
                  placeholder="enter skill"
                  error={errors.Name?.message}
                />
              </FormItem>
            </div>
            <div className="basis-1/2">
              <FormItem label="Score" htmlFor="score">
                <InputField
                  {...register('Score')}
                  name="Score"
                  type="number"
                  placeholder="enter score"
                  error={errors.Score?.message}
                />
              </FormItem>
            </div>
          </div>
          <div className="flex gap-4">
            {/* <div className="basis-1/2">
              <FormItem label="Subjects" htmlFor="subject">
                <SubjectDropdown
                  placeholder="Subject"
                  control={control}
                  name="Subject"
                  className="w-full max-w-60 bg-white"
                  multiple={true}
                  error={errors?.Subject?.message}
                />
              </FormItem>
            </div> */}
            <div className="basis-1/2">
              <FormItem label="Difficulty level" htmlFor="Difficulty">
                <SelectFieldHookForm
                  placeholder="selelct difficulty level"
                  id="Difficulty"
                  name="Difficulty"
                  className="w-full bg-white"
                  control={control}
                  options={difficultyLevelOptions}
                  error={errors?.Difficulty?.message}
                />
              </FormItem>
            </div>
            <div className="basis-1/2">
              <FormItem label="Duration (minutes)" htmlFor="duration">
                <InputField
                  {...register('Duration')}
                  name="Duration"
                  type="number"
                  placeholder="enter duration in minutes"
                  error={errors.Duration?.message}
                />
              </FormItem>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="basis-1/2 flex flex-col gap-2 mt-0">
              <label
                htmlFor="is_this_query"
                className="ml-2 text-sm font-medium text-gray-700"
              >
                Is This Query?
              </label>
              <div className="ml-2 w-fit">
                <Controller
                  name="is_this_query"
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      className={`${
                        value ? 'bg-[#0284c7]' : 'bg-gray-200 '
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          value ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  )}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col basis-1/2">
              <FormItem label="Description" htmlFor="description">
                <RichTextEditor
                  name="Description"
                  control={control}
                  error={errors?.Description?.message}
                />
              </FormItem>
            </div>
            <div className="flex flex-col basis-1/2">
              <FormItem label="Constrain" htmlFor="constrain">
                <RichTextEditor
                  name="Constrain"
                  control={control}
                  error={errors?.Constrain?.message}
                />
              </FormItem>
            </div>
          </div>

          <FormItem label="Example" htmlFor="example">
            <RichTextEditor
              name="Example"
              control={control}
              error={errors?.Example?.message}
            />
          </FormItem>
        </div>
        <div className="py-5">
          <Button
            type="submit"
            className="w-[200px]"
            loading={isAddQuestionLoading || isUpdateQuestionLoading}
          >
            {editMode ? 'Edit' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CodingQuestion;
